import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

/**
 * Modal that prompts for a 6-digit TOTP code. Used to retry a request that the
 * backend rejected with 401 {twoFactorRequired:true} (settings key changes,
 * withdrawals). Mirrors the Login.js 2FA step.
 *
 * props:
 * - open      : bool
 * - error     : string  — optional inline error (e.g. invalid code)
 * - onSubmit  : fn(totp) — called with the trimmed code
 * - onCancel  : fn
 */
function TwoFactorPrompt({ open, error, onSubmit, onCancel }) {
    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });
    const [totp, setTotp] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setTotp('');
            // focus after render
            setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 0);
        }
    }, [open]);

    if (!open) return null;

    function submit(e) {
        if (e) e.preventDefault();
        const code = (totp || '').toString().replace(/\s/g, '');
        if (!code) return;
        onSubmit(code);
    }

    return (
        <div
            className="modal d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{ background: 'rgba(0,0,0,0.6)' }}
            onClick={(e) => { if (e.target === e.currentTarget) onCancel && onCancel(); }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <form onSubmit={submit}>
                        <div className="modal-header">
                            <h5 className="modal-title">{t('settings.twoFaTitle')}</h5>
                            <button type="button" className="btn-close" aria-label={t('common.cancel')} onClick={onCancel}></button>
                        </div>
                        <div className="modal-body">
                            <p className="small text-muted mb-2">{t('settings.twoFaPrompt')}</p>
                            <input
                                ref={inputRef}
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={6}
                                placeholder="123456"
                                className="form-control num"
                                value={totp}
                                onChange={(e) => setTotp(e.target.value)} />
                            {error ? <div className="alert alert-danger mt-2 mb-0 py-2">{error}</div> : null}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={onCancel}>{t('common.cancel')}</button>
                            <button type="submit" className="btn btn-primary">{t('twofa.confirm')}</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default TwoFactorPrompt;
